import { useMemo } from 'react';
import { SportEventState } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useI18n as useI18nManager } from '@funtech-int/rival-react';

import { formatDate } from '@/shared/lib/date';

import { useI18n } from '../../hooks/useI18n';

interface SportStageBadgeTextOptions {
    eventState: SportEventState;
    startTime: string;
    endTime: string;
}

const DAYS_IN_WEEK = 7;

export function useStageBadgeText(options: SportStageBadgeTextOptions): string {
    const i18n = useI18n();
    const locale = useI18nManager().locale.get();
    const { lang } = locale;

    return useMemo(() => {
        const start = new Date(options.startTime);

        if (options.eventState === 'LIVE') {
            return i18n('on_air_text');
        }

        if (options.eventState === 'ANNOUNCE') {
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);

            if (start < endOfToday) {
                // HH:MM today
                return formatDate(start, lang, { hour: '2-digit', minute: '2-digit' });
            }

            const lastDayOf7Days = new Date().setDate(new Date().getDate() + (DAYS_IN_WEEK - 1));
            const endOfLastDayOf7Days = new Date(lastDayOf7Days);
            endOfLastDayOf7Days.setHours(23, 59, 59, 999);

            if (start < endOfLastDayOf7Days) {
                // "Mon 14:30"
                const weekDay = new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(start);
                const time = formatDate(start, lang, { hour: '2-digit', minute: '2-digit' });
                return [weekDay, time].join(' ');
            }

            // "15 Jan 14:30"
            const dateShort = formatDate(start, lang, { day: 'numeric', month: 'short' });
            const time = formatDate(start, lang, { hour: '2-digit', minute: '2-digit' });
            return [dateShort, time].join(' ');
        }

        // RECORD / EXPIRED
        const endDateShort = formatDate(new Date(options.endTime), lang, {
            day: 'numeric',
            month: 'short',
        });
        const completedLabel = i18n('completed_text');

        return `${completedLabel} (${endDateShort})`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.eventState, options.startTime, options.endTime, lang]);
}
